import { LOCALE_EN_US } from './constants';
import { AvailableLocale } from '@/i18n/LanguageProvider/shared';
import { AvailableLocales } from '@/utils/translations/constants';
import { ReactNode } from 'react';

/**
 * Reconcile a 2-character language code to the closest available locale.
 * If the language is already a full locale or has no match, return it unchanged.
 * @param {string | undefined} lang - 2-character or full 5-character locale code.
 * @returns {string | undefined} - The closest matching locale or the original input.
 */
export const reconcileLocale = (
  lang?: string | null,
): AvailableLocale | undefined => {
  if (!lang) {
    return undefined;
  }

  const locale = Object.values(AvailableLocales).find(
    locale => locale === lang,
  );
  if (locale) return locale;

  // If lang is a 2-character code, use explicit mapping to full locales
  if (lang.length === 2) {
    const twoLetterToFullLocale: Record<string, AvailableLocales> = {
      en: AvailableLocales.EN_US,
      fr: AvailableLocales.FR_FR, // can't infer fr-CA from 2-letter code
      es: AvailableLocales.ES_ES,
      it: AvailableLocales.IT_IT,
      pl: AvailableLocales.PL_PL,
      pt: AvailableLocales.PT_BR,
      ht: AvailableLocales.HT_HT,
      de: AvailableLocales.DE_DE,
      // Expanded language support (worker-facing)
      ar: AvailableLocales.AR,
      cs: AvailableLocales.CS_CZ,
      da: AvailableLocales.DA_DK,
      nl: AvailableLocales.NL_NL,
      tl: AvailableLocales.FP_PH,
      el: AvailableLocales.EL_GR,
      he: AvailableLocales.HE,
      hu: AvailableLocales.HU_HU,
      nb: AvailableLocales.NB_NO,
      no: AvailableLocales.NB_NO,
      ro: AvailableLocales.RO_RO,
      ru: AvailableLocales.RU_RU,
      sr: AvailableLocales.SR_RS,
      sv: AvailableLocales.SV_SE,
      tr: AvailableLocales.TR_TR,
      uk: AvailableLocales.UK_UA,
      vi: AvailableLocales.VI_VN,
      sq: AvailableLocales.SQ_AL,
      am: AvailableLocales.AM_ET,
      bn: AvailableLocales.BN,
      bs: AvailableLocales.BS_BA,
      bg: AvailableLocales.BG_BG,
      ca: AvailableLocales.CA,
      hr: AvailableLocales.HR_HR,
      fa: AvailableLocales.FA_IR, // default Farsi to Persian (Iran); Dari is fa-AF
      et: AvailableLocales.ET_EE,
      fi: AvailableLocales.FI_FI,
      ka: AvailableLocales.KA_GE,
      gu: AvailableLocales.GU_IN,
      kk: AvailableLocales.KK_KZ,
      lv: AvailableLocales.LV_LV,
      lt: AvailableLocales.LT_LT,
      ml: AvailableLocales.ML_IN,
      pa: AvailableLocales.PA_IN,
      sk: AvailableLocales.SK_SK,
      sl: AvailableLocales.SL_SI,
      so: AvailableLocales.SO_SO,
      ta: AvailableLocales.TA_IN,
      te: AvailableLocales.TE_IN,
      th: AvailableLocales.TH_TH,
      ur: AvailableLocales.UR,
      af: AvailableLocales.AF_ZA,
      hy: AvailableLocales.HY_AM,
      az: AvailableLocales.AZ_AZ,
      ha: AvailableLocales.HA,
      id: AvailableLocales.ID_ID,
      kn: AvailableLocales.KN_IN,
      mk: AvailableLocales.MK_MK,
      ms: AvailableLocales.MS,
      mr: AvailableLocales.MR_IN,
      mn: AvailableLocales.MN_MN,
      ps: AvailableLocales.PS,
      si: AvailableLocales.SI_LK,
      sw: AvailableLocales.SW,
      uz: AvailableLocales.UZ_UZ,
    };

    const fullLocale = twoLetterToFullLocale[lang.toLowerCase()];
    if (fullLocale) return fullLocale;
  }

  // For other partial matches, attempt to match to available locales
  const matchedLocale = Object.values(AvailableLocales).find(locale =>
    locale.startsWith(lang),
  );
  return matchedLocale ?? undefined;
};

export const isSupportedLocale = (lang: string): boolean => {
  const reconciledLang = reconcileLocale(lang) || LOCALE_EN_US;
  return Object.values(AvailableLocales).includes(
    reconciledLang as AvailableLocale,
  );
};

/**
 * Type guard for AvailableLocales.
 * Performs a runtime check and narrows the type for the caller.
 */
export const isAvailableLocale = (val: string): val is AvailableLocales => {
  return Object.values(AvailableLocales).includes(val as AvailableLocales);
};

/**
 * Normalizes locale format from underscore (en_US) to dash (en-US) format.
 * If locale is already in dash format, returns it unchanged.
 * @param {string | undefined} locale - The locale string in either format
 * @returns {AvailableLocales} - The normalized locale in dash format, or LOCALE_EN_US as fallback
 */
export const normalizeLocale = (locale?: string): AvailableLocales => {
  if (!locale) {
    return LOCALE_EN_US;
  }

  // Convert underscore to dash format (e.g., en_US -> en-US)
  const normalized = locale.replace('_', '-');

  // Validate that the normalized locale is supported using a type guard
  if (isAvailableLocale(normalized)) {
    return normalized;
  }

  // Try to reconcile partial matches (e.g., 'en' -> 'en-US', 'fr' -> 'fr-FR')
  const reconciled = reconcileLocale(normalized);
  if (reconciled) {
    return reconciled;
  }

  // Fallback to default locale
  return LOCALE_EN_US;
};

export const AvailableLocalesDetails: Record<
  AvailableLocales,
  { label: ReactNode }
> = {
  [AvailableLocales.FR_FR]: {
    label: 'Français',
  },
  [AvailableLocales.EN_US]: {
    label: 'English',
  },
  [AvailableLocales.ES_ES]: {
    label: 'Español',
  },
  [AvailableLocales.ES_MX]: {
    label: 'Español (México)',
  },
  [AvailableLocales.IT_IT]: {
    label: 'Italiano',
  },
  [AvailableLocales.PL_PL]: {
    label: 'Polski',
  },
  [AvailableLocales.PT_BR]: {
    label: 'Português',
  },
  [AvailableLocales.HT_HT]: {
    label: 'Kreyòl',
  },
  [AvailableLocales.FR_CA]: {
    label: 'Français (Canada)',
  },
  [AvailableLocales.DE_DE]: {
    label: 'Deutsch',
  },
  [AvailableLocales.JA_JP]: {
    label: '日本語',
  },
  [AvailableLocales.HI_IN]: {
    label: 'हिंदी',
  },
  [AvailableLocales.ZH_CN]: {
    label: '中文 (简体)', // Simplified Chinese (Mandarin)
  },
  [AvailableLocales.ZH_TW]: {
    label: '中文 (繁體)', // Traditional Chinese
  },
  [AvailableLocales.KO_KR]: {
    label: '한국어',
  },
  // Expanded language support (worker-facing)
  [AvailableLocales.AR]: {
    label: 'العربية',
  },
  [AvailableLocales.ZH_HK]: {
    label: '中文 (香港)', // Traditional Chinese (Hong Kong)
  },
  [AvailableLocales.CS_CZ]: {
    label: 'Čeština',
  },
  [AvailableLocales.DA_DK]: {
    label: 'Dansk',
  },
  [AvailableLocales.NL_NL]: {
    label: 'Nederlands (Dutch)',
  },
  [AvailableLocales.FP_PH]: {
    label: 'Filipino',
  },
  [AvailableLocales.EL_GR]: {
    label: 'Ελληνικά',
  },
  [AvailableLocales.HE]: {
    label: 'עברית',
  },
  [AvailableLocales.HU_HU]: {
    label: 'Magyar',
  },
  [AvailableLocales.NB_NO]: {
    label: 'Norsk (Bokmål)',
  },
  [AvailableLocales.PT_PT]: {
    label: 'Português (Portugal)',
  },
  [AvailableLocales.RO_RO]: {
    label: 'Română',
  },
  [AvailableLocales.RU_RU]: {
    label: 'Русский',
  },
  [AvailableLocales.SR_RS]: {
    label: 'Српски',
  },
  [AvailableLocales.SV_SE]: {
    label: 'Svenska',
  },
  [AvailableLocales.TR_TR]: {
    label: 'Türkçe',
  },
  [AvailableLocales.UK_UA]: {
    label: 'Українська',
  },
  [AvailableLocales.VI_VN]: {
    label: 'Tiếng Việt',
  },
  [AvailableLocales.SQ_AL]: {
    label: 'Shqip',
  },
  [AvailableLocales.AM_ET]: {
    label: 'አማርኛ',
  },
  [AvailableLocales.BN]: {
    label: 'বাংলা',
  },
  [AvailableLocales.BS_BA]: {
    label: 'Bosanski',
  },
  [AvailableLocales.BG_BG]: {
    label: 'Български',
  },
  [AvailableLocales.CA]: {
    label: 'Català',
  },
  [AvailableLocales.HR_HR]: {
    label: 'Hrvatski',
  },
  [AvailableLocales.FA_AF]: {
    label: 'دری', // Dari
  },
  [AvailableLocales.ET_EE]: {
    label: 'Eesti',
  },
  [AvailableLocales.FA_IR]: {
    label: 'فارسی',
  },
  [AvailableLocales.FI_FI]: {
    label: 'Suomi',
  },
  [AvailableLocales.KA_GE]: {
    label: 'ქართული',
  },
  [AvailableLocales.GU_IN]: {
    label: 'ગુજરાતી',
  },
  [AvailableLocales.KK_KZ]: {
    label: 'Қазақша',
  },
  [AvailableLocales.LV_LV]: {
    label: 'Latviešu',
  },
  [AvailableLocales.LT_LT]: {
    label: 'Lietuvių',
  },
  [AvailableLocales.ML_IN]: {
    label: 'മലയാളം',
  },
  [AvailableLocales.PA_IN]: {
    label: 'ਪੰਜਾਬੀ',
  },
  [AvailableLocales.SK_SK]: {
    label: 'Slovenčina',
  },
  [AvailableLocales.SL_SI]: {
    label: 'Slovenščina',
  },
  [AvailableLocales.SO_SO]: {
    label: 'Soomaali',
  },
  [AvailableLocales.TA_IN]: {
    label: 'தமிழ்',
  },
  [AvailableLocales.TE_IN]: {
    label: 'తెలుగు',
  },
  [AvailableLocales.TH_TH]: {
    label: 'ภาษาไทย',
  },
  [AvailableLocales.UR]: {
    label: 'اردو',
  },
  [AvailableLocales.AF_ZA]: {
    label: 'Afrikaans',
  },
  [AvailableLocales.HY_AM]: {
    label: 'Հայերեն',
  },
  [AvailableLocales.AZ_AZ]: {
    label: 'Azərbaycan',
  },
  [AvailableLocales.HA]: {
    label: 'Hausa',
  },
  [AvailableLocales.ID_ID]: {
    label: 'Bahasa Indonesia',
  },
  [AvailableLocales.KN_IN]: {
    label: 'ಕನ್ನಡ',
  },
  [AvailableLocales.MK_MK]: {
    label: 'Македонски',
  },
  [AvailableLocales.MS]: {
    label: 'Bahasa Melayu',
  },
  [AvailableLocales.MR_IN]: {
    label: 'मराठी',
  },
  [AvailableLocales.MN_MN]: {
    label: 'Монгол',
  },
  [AvailableLocales.PS]: {
    label: 'پښتو',
  },
  [AvailableLocales.SI_LK]: {
    label: 'සිංහල',
  },
  [AvailableLocales.SW]: {
    label: 'Kiswahili',
  },
  [AvailableLocales.UZ_UZ]: {
    label: 'Oʻzbekcha',
  },
};

export function getLocaleDisplayLabel(locale?: string | null): string {
  if (!locale) return '';
  const normalized = String(locale).replace('_', '-');
  const entry = (
    AvailableLocalesDetails as Record<string, { label: ReactNode }>
  )[normalized];
  if (entry) {
    const lbl = entry.label;
    return typeof lbl === 'string' ? lbl : String(lbl);
  }
  return normalized;
}

/**
 * Extracts the language code from a full locale string.
 * MUI date pickers expect language codes (e.g., 'en', 'fr') rather than
 * full locales (e.g., 'en-US', 'fr-FR'). Underscore forms from storage
 * (e.g., 'fr_FR') are normalized first.
 * Examples: 'en-US' -> 'en', 'fr-FR' -> 'fr', 'fr_FR' -> 'fr'
 */
export const getLanguageCode = (locale: AvailableLocale | string): string => {
  return normalizeLocale(locale).split('-')[0];
};
